// pages/MeetingRoom.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import webrtcService from '../services/webrtcService';
import socketService from '../services/socketService';
import {
  Mic, MicOff, Video, VideoOff, Phone, MessageSquare, Users, 
  Monitor, Copy, Check, X, AlertCircle, MonitorOff, RefreshCw
} from 'lucide-react';

const MeetingRoom = () => {
  const { trainingId, roomId } = useParams();
  const navigate = useNavigate();
  
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [participants, setParticipants] = useState([]);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [mediaMode, setMediaMode] = useState('full'); // 'full', 'audio-only', 'viewer'
  const [deviceInfo, setDeviceInfo] = useState(null);
  
  const localVideoRef = useRef(null);
  const remoteVideosRef = useRef(new Map());
  const screenStreamRef = useRef(null);
  const originalVideoTrackRef = useRef(null);
  const isFirstMount = useRef(true);
  
  // Get current user with fallback
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        return {
          id: `guest-${Date.now()}`,
          name: 'Guest User',
          role: 'participant'
        };
      }
      
      const user = JSON.parse(userStr);
      return {
        id: user.id || user._id || `user-${Date.now()}`,
        name: user.name || user.username || user.email || 'User',
        role: user.role || 'participant'
      };
    } catch (error) {
      console.error('Error parsing user data:', error);
      return {
        id: `guest-${Date.now()}`,
        name: 'Guest User',
        role: 'participant'
      };
    }
  };
  
  const currentUser = getCurrentUser();

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      initializeMeeting();
    }
    
    return () => {
      cleanup();
    };
  }, []);

  const initializeMeeting = async () => {
    try {
      setIsInitializing(true);
      
      console.log('🚀 ========================================');
      console.log('🚀 INITIALIZING MEETING');
      console.log('🚀 User:', currentUser);
      console.log('🚀 Room ID:', roomId);
      console.log('🚀 Training ID:', trainingId);
      console.log('🚀 ========================================');
      
      // Check available devices first
      const devices = await webrtcService.getAvailableDevices();
      setDeviceInfo(devices);
      
      console.log('📱 Device check:', {
        hasCamera: devices.hasCamera,
        hasMicrophone: devices.hasMicrophone,
        cameras: devices.cameras.map(c => c.label || 'Unknown Camera'),
        microphones: devices.microphones.map(m => m.label || 'Unknown Microphone')
      });

      if (!devices.hasCamera && !devices.hasMicrophone) {
        throw new Error('No camera or microphone found on your device');
      }

      // Try to get media with fallback
      console.log('🎥 Attempting to get media stream with fallback...');
      const result = await webrtcService.initializeWithFallback();

      if (result.success && result.stream) {
        console.log('✅ Media stream acquired successfully');
        
        setLocalStream(result.stream);
        
        // Update states based on what we got
        setIsVideoEnabled(result.hasVideo);
        setIsAudioEnabled(result.hasAudio);
        
        if (!result.hasVideo && result.hasAudio) {
          setMediaMode('audio-only');
          console.log('📱 Mode: Audio Only');
        } else if (result.hasVideo && result.hasAudio) {
          setMediaMode('full');
          console.log('📱 Mode: Full (Video + Audio)');
        }
        
        // Attach to video element
        if (localVideoRef.current && result.stream) {
          localVideoRef.current.srcObject = result.stream;
          console.log('✅ Local stream attached to video element');
          
          // Wait for video to load
          localVideoRef.current.onloadedmetadata = () => {
            console.log('✅ Video metadata loaded');
            localVideoRef.current.play().catch(err => {
              console.warn('⚠️ Autoplay prevented:', err);
            });
          };
        }
      } else {
        // No media at all - join as viewer
        console.warn('⚠️ Could not acquire any media, joining as viewer');
        setMediaMode('viewer');
        setIsVideoEnabled(false);
        setIsAudioEnabled(false);
        
        setError({
          type: 'warning',
          message: 'Joined as viewer. Camera and microphone are not available. ' + (result.message || '')
        });
      }

      // Connect to signaling server
      const signalingUrl = process.env.REACT_APP_SIGNALING_URL || 'http://localhost:3001';
      
      console.log('📡 Connecting to signaling server:', signalingUrl);
      
      try {
        socketService.connect(
          signalingUrl, 
          roomId, 
          currentUser.id, 
          currentUser.name
        );
        
        // Wait for connection
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Connection timeout'));
          }, 10000);
          
          const checkConnection = setInterval(() => {
            if (socketService.isConnected()) {
              clearInterval(checkConnection);
              clearTimeout(timeout);
              resolve();
            }
          }, 100);
        });
        
        console.log('✅ Connected to signaling server');

        // Setup socket listeners
        setupSocketListeners();

        // Join room
        console.log('🚪 Joining room...');
        socketService.joinRoom(roomId, {
          userId: currentUser.id,
          userName: currentUser.name,
          userRole: currentUser.role,
          mediaMode
        });

        // Fetch meeting info
        fetchMeetingInfo();
        
        setIsInitializing(false);
        console.log('✅ Meeting initialization complete');
      } catch (socketError) {
        console.error('❌ Signaling server error:', socketError);
        setError({
          type: 'error',
          message: 'Failed to connect to meeting server. Please check your internet connection and ensure the signaling server is running at ' + signalingUrl
        });
        setIsInitializing(false);
      }
    } catch (error) {
      console.error('❌ Error initializing meeting:', error);
      setError({
        type: 'error',
        message: error.message || 'Failed to initialize meeting. Please refresh and try again.'
      });
      setIsInitializing(false);
    }
  };

  const setupSocketListeners = () => {
    console.log('🔌 Setting up socket listeners...');
    
    // New user joined
    socketService.on('user-joined', async ({ userId, userName, userRole }) => {
      console.log('👤 User joined:', userName, '(', userId, ')');
      
      setParticipants(prev => {
        if (prev.some(p => p.userId === userId)) {
          console.log('⚠️ User already in participants list');
          return prev;
        }
        return [...prev, { 
          userId, 
          userName, 
          userRole, 
          mediaState: { video: true, audio: true },
          isScreenSharing: false
        }];
      });
      
      // Only create peer connection if we have local stream
      const localStream = webrtcService.getLocalStream();
      if (!localStream) {
        console.log('⚠️ No local stream, skipping peer connection for', userName);
        return;
      }

      try {
        console.log('🔗 Creating peer connection for', userName);
        
        const peerConnection = webrtcService.createPeerConnection(userId, {
          onIceCandidate: (candidate) => {
            socketService.sendIceCandidate(userId, candidate);
          },
          onTrack: (event) => {
            console.log('📥 Received track from', userName);
            const [remoteStream] = event.streams;
            setRemoteStreams(prev => new Map(prev).set(userId, remoteStream));
          },
          onConnectionStateChange: (state) => {
            console.log(`🔗 Connection with ${userName}:`, state);
          }
        });

        // Create and send offer
        console.log('📤 Creating offer for', userName);
        const offer = await peerConnection.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true
        });
        await peerConnection.setLocalDescription(offer);
        socketService.sendOffer(userId, offer);
        console.log('✅ Offer sent to', userName);
      } catch (error) {
        console.error('❌ Error creating peer connection:', error);
      }
    });

    // Receive offer
    socketService.on('offer', async ({ peerId, offer }) => {
      console.log('📨 Received offer from peer:', peerId);
      
      try {
        const peerConnection = webrtcService.createPeerConnection(peerId, {
          onIceCandidate: (candidate) => {
            socketService.sendIceCandidate(peerId, candidate);
          },
          onTrack: (event) => {
            console.log('📥 Received track from peer', peerId);
            const [remoteStream] = event.streams;
            setRemoteStreams(prev => new Map(prev).set(peerId, remoteStream));
          }
        });

        await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(answer);
        socketService.sendAnswer(peerId, answer);
        console.log('✅ Answer sent to peer', peerId);
      } catch (error) {
        console.error('❌ Error handling offer:', error);
      }
    });

    // Receive answer
    socketService.on('answer', async ({ peerId, answer }) => {
      console.log('📨 Received answer from peer:', peerId);
      
      try {
        const peerConnection = webrtcService.getPeerConnection(peerId);
        if (peerConnection) {
          await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
          console.log('✅ Remote description set for peer', peerId);
        }
      } catch (error) {
        console.error('❌ Error handling answer:', error);
      }
    });

    // Receive ICE candidate
    socketService.on('ice-candidate', async ({ peerId, candidate }) => {
      try {
        const peerConnection = webrtcService.getPeerConnection(peerId);
        if (peerConnection && peerConnection.remoteDescription) {
          await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
        }
      } catch (error) {
        console.error('❌ Error adding ICE candidate:', error);
      }
    });

    // User left
    socketService.on('user-left', ({ userId, userName }) => {
      console.log('👋 User left:', userName);
      setParticipants(prev => prev.filter(p => p.userId !== userId));
      setRemoteStreams(prev => {
        const newMap = new Map(prev);
        newMap.delete(userId);
        return newMap;
      });
      webrtcService.removePeerConnection(userId);
    });

    // Chat message
    socketService.on('chat-message', ({ userId, userName, message, timestamp }) => {
      setMessages(prev => [...prev, { 
        userId, 
        userName, 
        message, 
        timestamp, 
        isSelf: userId === currentUser.id 
      }]);
    });

    // Media state change
    socketService.on('media-state-change', ({ userId, mediaState }) => {
      console.log('🎥 Media state change for user', userId, mediaState);
      setParticipants(prev => prev.map(p => 
        p.userId === userId ? { ...p, mediaState } : p
      ));
    });

    // Screen share state change
    socketService.on('screen-share-change', ({ userId, isSharing }) => {
      console.log('🖥️ Screen share change for user', userId, isSharing);
      setParticipants(prev => prev.map(p => 
        p.userId === userId ? { ...p, isScreenSharing: isSharing } : p
      ));
    });

    // Room info
    socketService.on('room-info', ({ participants: roomParticipants }) => {
      console.log('📊 Room info received:', roomParticipants);
      setParticipants(roomParticipants);
    });

    // Reconnection
    socketService.on('reconnect', () => {
      console.log('🔄 Reconnected to signaling server');
      setError(null);
      socketService.joinRoom(roomId, {
        userId: currentUser.id,
        userName: currentUser.name,
        userRole: currentUser.role,
        mediaMode
      });
    });
    
    console.log('✅ Socket listeners configured');
  };

  const fetchMeetingInfo = async () => {
    try {
      const response = await fetch(`/api/training/${trainingId}`);
      if (response.ok) {
        const data = await response.json();
        setMeetingInfo(data);
      }
    } catch (error) {
      console.error('Error fetching meeting info:', error);
    }
  };

  const toggleVideo = useCallback(() => {
    console.log('🎬 Toggle video clicked');
    
    if (mediaMode === 'viewer') {
      alert('Camera access is not available. Please refresh the page and grant camera permissions.');
      return;
    }
    
    if (isSharingScreen) {
      alert('Stop screen sharing first to control your camera.');
      return;
    }
    
    const newState = webrtcService.toggleVideo();
    setIsVideoEnabled(newState);
    socketService.updateMediaState(roomId, { video: newState, audio: isAudioEnabled });
    
    console.log('📹 Video is now:', newState ? 'ON' : 'OFF');
  }, [mediaMode, isSharingScreen, roomId, isAudioEnabled]);

  const toggleAudio = useCallback(() => {
    console.log('🎤 Toggle audio clicked');
    
    if (mediaMode === 'viewer') {
      alert('Microphone access is not available. Please refresh the page and grant microphone permissions.');
      return;
    }
    
    const newState = webrtcService.toggleAudio();
    setIsAudioEnabled(newState);
    socketService.updateMediaState(roomId, { video: isVideoEnabled, audio: newState });
    
    console.log('🎤 Audio is now:', newState ? 'ON' : 'OFF');
  }, [mediaMode, roomId, isVideoEnabled]);

  const shareScreen = useCallback(async () => {
    console.log('🖥️ Screen share clicked, current state:', isSharingScreen);
    
    if (isSharingScreen) {
      console.log('⏹️ Stopping screen share...');
      
      // Stop screen sharing - revert to camera
      const localStream = webrtcService.getLocalStream();
      if (localStream && originalVideoTrackRef.current) {
        await webrtcService.replaceVideoTrack(localStream);
        
        // Update local video element
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStream;
        }
      }
      
      // Clean up screen stream
      webrtcService.stopScreenStream();
      screenStreamRef.current = null;
      originalVideoTrackRef.current = null;
      
      setIsSharingScreen(false);
      socketService.updateScreenShareState(roomId, false);
      console.log('✅ Screen share stopped');
      
    } else {
      console.log('▶️ Starting screen share...');
      
      // Save original video track
      const localStream = webrtcService.getLocalStream();
      if (localStream && localStream.getVideoTracks().length > 0) {
        originalVideoTrackRef.current = localStream.getVideoTracks()[0];
        console.log('💾 Saved original video track');
      }
      
      // Start screen sharing
      const result = await webrtcService.startScreenShare();
      
      if (result.success && result.stream) {
        screenStreamRef.current = result.stream;
        
        // Replace video track for all peers
        await webrtcService.replaceVideoTrack(result.stream);
        
        // Update local video element to show screen share
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = result.stream;
        }
        
        setIsSharingScreen(true);
        socketService.updateScreenShareState(roomId, true);
        console.log('✅ Screen share started');
        
        // Handle screen share stop (when user clicks "Stop Sharing" in browser)
        result.stream.getVideoTracks()[0].onended = async () => {
          console.log('🖥️ Screen share ended by user via browser UI');
          
          // Revert to camera
          const localStream = webrtcService.getLocalStream();
          if (localStream) {
            await webrtcService.replaceVideoTrack(localStream);
            
            if (localVideoRef.current) {
              localVideoRef.current.srcObject = localStream;
            }
          }
          
          webrtcService.stopScreenStream();
          screenStreamRef.current = null;
          originalVideoTrackRef.current = null;
          
          setIsSharingScreen(false);
          socketService.updateScreenShareState(roomId, false);
        };
        
      } else {
        console.error('❌ Screen share failed:', result.error);
        alert(result.error || 'Failed to share screen. Please try again.');
      }
    }
  }, [isSharingScreen, roomId]);

  const retryMediaAccess = async () => {
    console.log('🔄 Retrying media access...');
    setIsInitializing(true);
    setError(null);
    
    try {
      const result = await webrtcService.initializeWithFallback();
      
      if (result.success && result.stream) {
        setLocalStream(result.stream);
        setIsVideoEnabled(result.hasVideo);
        setIsAudioEnabled(result.hasAudio);
        
        if (result.hasVideo && result.hasAudio) {
          setMediaMode('full');
        } else if (!result.hasVideo && result.hasAudio) {
          setMediaMode('audio-only');
        }
        
        if (localVideoRef.current && result.stream) {
          localVideoRef.current.srcObject = result.stream;
        }
        
        // Update media state to all peers
        socketService.updateMediaState(roomId, { 
          video: result.hasVideo, 
          audio: result.hasAudio 
        });
        
        console.log('✅ Media access retry successful');
      } else {
        setError({
          type: 'warning',
          message: result.message || 'Could not access camera/microphone'
        });
      }
    } catch (error) {
      console.error('❌ Media retry failed:', error);
      setError({
        type: 'warning',
        message: 'Failed to access camera/microphone. ' + error.message
      });
    } finally {
      setIsInitializing(false);
    }
  };

  const sendMessage = useCallback(() => {
    if (messageInput.trim()) {
      socketService.sendMessage(messageInput, roomId);
      setMessageInput('');
    }
  }, [messageInput, roomId]);

  const copyMeetingLink = () => {
    const link = `${window.location.origin}/meeting/${trainingId}/${roomId}`;
    navigator.clipboard.writeText(link);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const leaveMeeting = () => {
    if (window.confirm('Are you sure you want to leave the meeting?')) {
      cleanup();
      navigate('/trainings');
    }
  };

  const cleanup = () => {
    console.log('🧹 Cleaning up meeting...');
    
    if (isSharingScreen) {
      webrtcService.stopScreenStream();
    }
    
    socketService.leaveRoom(roomId);
    socketService.disconnect();
    webrtcService.closeAllConnections();
    
    screenStreamRef.current = null;
    originalVideoTrackRef.current = null;
    
    console.log('✅ Cleanup complete');
  };

  // Styles remain the same as before
  const styles = {
    container: {
      height: '100vh',
      backgroundColor: '#0f172a',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      padding: '16px 24px',
      backgroundColor: '#1e293b',
      borderBottom: '1px solid #334155',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    meetingTitle: {
      color: '#fff',
      fontSize: '18px',
      fontWeight: '600',
      margin: 0
    },
    meetingTime: {
      color: '#94a3b8',
      fontSize: '14px',
      marginTop: '4px'
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      overflow: 'hidden',
      minHeight: 0
    },
    videoGrid: {
      flex: 1,
      display: 'grid',
      gap: '8px',
      padding: '16px',
      overflow: 'auto'
    },
    videoContainer: {
      position: 'relative',
      backgroundColor: '#1e293b',
      borderRadius: '12px',
      overflow: 'hidden',
      aspectRatio: '16/9',
      minHeight: '200px'
    },
    video: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    videoOverlay: {
      position: 'absolute',
      bottom: '12px',
      left: '12px',
      right: '12px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      zIndex: 10
    },
    participantName: {
      color: '#fff',
      fontSize: '14px',
      fontWeight: '500',
      backgroundColor: 'rgba(0,0,0,0.6)',
      padding: '6px 12px',
      borderRadius: '6px',
      backdropFilter: 'blur(4px)'
    },
    participantAvatar: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#3b82f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: '600',
      fontSize: '32px'
    },
    sidebar: {
      width: '350px',
      backgroundColor: '#1e293b',
      borderLeft: '1px solid #334155',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0
    },
    sidebarHeader: {
      padding: '16px',
      borderBottom: '1px solid #334155',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    sidebarTitle: {
      color: '#fff',
      fontSize: '16px',
      fontWeight: '600',
      margin: 0
    },
    chatMessages: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
      minHeight: 0
    },
    message: {
      marginBottom: '16px'
    },
    messageSender: {
      fontSize: '12px',
      fontWeight: '500',
      color: '#94a3b8',
      marginBottom: '4px'
    },
    messageText: {
      backgroundColor: '#334155',
      color: '#fff',
      padding: '8px 12px',
      borderRadius: '8px',
      fontSize: '14px',
      wordBreak: 'break-word'
    },
    chatInput: {
      padding: '16px',
      borderTop: '1px solid #334155',
      flexShrink: 0
    },
    input: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#334155',
      border: 'none',
      borderRadius: '8px',
      color: '#fff',
      fontSize: '14px',
      outline: 'none'
    },
    controls: {
      padding: '20px',
      backgroundColor: '#1e293b',
      borderTop: '1px solid #334155',
      display: 'flex',
      justifyContent: 'center',
      gap: '12px',
      flexShrink: 0,
      flexWrap: 'wrap'
    },
    controlButton: {
      width: '56px',
      height: '56px',
      borderRadius: '50%',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      backgroundColor: '#334155',
      flexShrink: 0
    },
    activeButton: {
      backgroundColor: '#3b82f6'
    },
    dangerButton: {
      backgroundColor: '#ef4444'
    },
    participantsList: {
      padding: '16px',
      overflowY: 'auto',
      minHeight: 0
    },
    participant: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      backgroundColor: '#334155',
      borderRadius: '8px',
      marginBottom: '8px'
    },
    participantInfo: {
      flex: 1,
      minWidth: 0
    },
    participantNameText: {
      color: '#fff',
      fontSize: '14px',
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    participantStatus: {
      color: '#94a3b8',
      fontSize: '12px',
      marginTop: '2px'
    },
    errorBanner: {
      padding: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      flexShrink: 0
    },
    errorWarning: {
      backgroundColor: '#fbbf24',
      color: '#78350f'
    },
    errorDanger: {
      backgroundColor: '#ef4444',
      color: '#fff'
    },
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    loadingContent: {
      textAlign: 'center',
      color: '#fff'
    },
    spinner: {
      border: '4px solid #334155',
      borderTop: '4px solid #3b82f6',
      borderRadius: '50%',
      width: '50px',
      height: '50px',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 20px'
    },
    screenShareBadge: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      backgroundColor: '#10b981',
      color: '#fff',
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      zIndex: 10
    }
  };

  const totalVideos = remoteStreams.size + (mediaMode !== 'viewer' ? 1 : 0);
  const gridCols = Math.min(Math.ceil(Math.sqrt(totalVideos)), 3);
  const gridTemplate = {
    gridTemplateColumns: `repeat(${gridCols}, 1fr)`
  };

  if (error && error.type === 'error') {
    return (
      <div style={{ ...styles.container, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#ef4444', maxWidth: '500px', padding: '20px' }}>
          <AlertCircle size={64} style={{ margin: '0 auto 20px' }} />
          <h2 style={{ marginBottom: '10px' }}>Unable to Join Meeting</h2>
          <p style={{ color: '#94a3b8', marginBottom: '20px' }}>{error.message}</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              onClick={() => window.location.reload()} 
              style={{ 
                padding: '10px 24px',
                backgroundColor: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Retry
            </button>
            <button 
              onClick={() => navigate('/trainings')} 
              style={{ 
                padding: '10px 24px',
                backgroundColor: '#334155',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Back to Trainings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {isInitializing && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingContent}>
            <div style={styles.spinner}></div>
            <h3>Joining Meeting...</h3>
            <p style={{ color: '#94a3b8' }}>Setting up your connection</p>
          </div>
        </div>
      )}

      {error && error.type === 'warning' && (
        <div style={{ ...styles.errorBanner, ...styles.errorWarning }}>
          <AlertCircle size={20} />
          <span>{error.message}</span>
          <button
            onClick={retryMediaAccess}
            style={{
              marginLeft: '12px',
              padding: '6px 12px',
              backgroundColor: 'rgba(0,0,0,0.2)',
              border: 'none',
              borderRadius: '6px',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <RefreshCw size={14} />
            Retry
          </button>
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.meetingTitle}>
            {meetingInfo?.trainingName || 'Training Meeting'}
          </h1>
          <div style={styles.meetingTime}>
            {participants.length + 1} participant{participants.length !== 0 ? 's' : ''}
            {mediaMode === 'audio-only' && ' • Audio Only'}
            {mediaMode === 'viewer' && ' • Viewer Mode'}
            {isSharingScreen && ' • Screen Sharing'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={copyMeetingLink}
            style={{
              padding: '8px 16px',
              backgroundColor: copySuccess ? '#10b981' : '#334155',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            {copySuccess ? <Check size={16} /> : <Copy size={16} />}
            {copySuccess ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      </div>

      <div style={styles.mainContent}>
        <div style={{ ...styles.videoGrid, ...gridTemplate }}>
          {mediaMode !== 'viewer' && (
            <div style={styles.videoContainer}>
              {isSharingScreen && (
                <div style={styles.screenShareBadge}>
                  <Monitor size={14} />
                  Sharing Screen
                </div>
              )}
              
              {(isVideoEnabled || isSharingScreen) ? (
                <video
                  ref={localVideoRef}
                  autoPlay
                  muted
                  playsInline
                  style={{ 
                    ...styles.video, 
                    transform: isSharingScreen ? 'none' : 'scaleX(-1)'
                  }}
                />
              ) : (
                <div style={{
                  ...styles.video,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#1e293b'
                }}>
                  <div style={styles.participantAvatar}>
                    {currentUser.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                </div>
              )}
              
              <div style={styles.videoOverlay}>
                <div style={styles.participantName}>
                  You
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {!isVideoEnabled && !isSharingScreen && (
                    <div style={{ backgroundColor: 'rgba(239,68,68,0.9)', padding: '6px', borderRadius: '6px' }}>
                      <VideoOff size={16} color="#fff" />
                    </div>
                  )}
                  {!isAudioEnabled && (
                    <div style={{ backgroundColor: 'rgba(239,68,68,0.9)', padding: '6px', borderRadius: '6px' }}>
                      <MicOff size={16} color="#fff" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {Array.from(remoteStreams.entries()).map(([userId, stream]) => {
            const participant = participants.find(p => p.userId === userId);
            const hasVideo = stream.getVideoTracks().length > 0 && 
                           stream.getVideoTracks()[0].enabled;
            
            return (
              <div key={userId} style={styles.videoContainer}>
                {participant?.isScreenSharing && (
                  <div style={styles.screenShareBadge}>
                    <Monitor size={14} />
                    Sharing Screen
                  </div>
                )}
                
                {hasVideo ? (
                  <video
                    ref={(el) => {
                      if (el && stream) {
                        el.srcObject = stream;
                        remoteVideosRef.current.set(userId, el);
                      }
                    }}
                    autoPlay
                    playsInline
                    style={styles.video}
                  />
                ) : (
                  <div style={{
                    ...styles.video,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#1e293b'
                  }}>
                    <div style={styles.participantAvatar}>
                      {participant?.userName?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  </div>
                )}
                
                <div style={styles.videoOverlay}>
                  <div style={styles.participantName}>
                    {participant?.userName || 'Unknown'}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {!participant?.mediaState?.video && !participant?.isScreenSharing && (
                      <div style={{ backgroundColor: 'rgba(239,68,68,0.9)', padding: '6px', borderRadius: '6px' }}>
                        <VideoOff size={16} color="#fff" />
                      </div>
                    )}
                    {!participant?.mediaState?.audio && (
                      <div style={{ backgroundColor: 'rgba(239,68,68,0.9)', padding: '6px', borderRadius: '6px' }}>
                        <MicOff size={16} color="#fff" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {(showChat || showParticipants) && (
          <div style={styles.sidebar}>
            <div style={styles.sidebarHeader}>
              <h3 style={styles.sidebarTitle}>
                {showChat ? 'Chat' : 'Participants'}
              </h3>
              <button
                onClick={() => {
                  setShowChat(false);
                  setShowParticipants(false);
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {showChat && (
              <>
                <div style={styles.chatMessages}>
                  {messages.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>
                      No messages yet. Start the conversation!
                    </div>
                  ) : (
                    messages.map((msg, index) => (
                      <div key={index} style={styles.message}>
                        <div style={styles.messageSender}>
                          {msg.isSelf ? 'You' : msg.userName}
                        </div>
                        <div style={{
                          ...styles.messageText,
                          backgroundColor: msg.isSelf ? '#3b82f6' : '#334155'
                        }}>
                          {msg.message}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div style={styles.chatInput}>
                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    style={styles.input}
                  />
                </div>
              </>
            )}

            {showParticipants && (
              <div style={styles.participantsList}>
                <div style={styles.participant}>
                  <div style={{ ...styles.participantAvatar, width: '40px', height: '40px', fontSize: '16px' }}>
                    {currentUser.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div style={styles.participantInfo}>
                    <div style={styles.participantNameText}>
                      You ({currentUser.name})
                    </div>
                    <div style={styles.participantStatus}>
                      {currentUser.role || 'Participant'} 
                      {mediaMode !== 'full' && ` • ${mediaMode}`}
                      {isSharingScreen && ' • Sharing'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    {isAudioEnabled ? (
                      <Mic size={16} color="#10b981" />
                    ) : (
                      <MicOff size={16} color="#ef4444" />
                    )}
                    {isVideoEnabled || isSharingScreen ? (
                      isSharingScreen ? (
                        <Monitor size={16} color="#10b981" />
                      ) : (
                        <Video size={16} color="#10b981" />
                      )
                    ) : (
                      <VideoOff size={16} color="#ef4444" />
                    )}
                  </div>
                </div>

                {participants.map((participant) => (
                  <div key={participant.userId} style={styles.participant}>
                    <div style={{ ...styles.participantAvatar, width: '40px', height: '40px', fontSize: '16px' }}>
                      {participant.userName.charAt(0).toUpperCase()}
                    </div>
                    <div style={styles.participantInfo}>
                      <div style={styles.participantNameText}>
                        {participant.userName}
                      </div>
                      <div style={styles.participantStatus}>
                        {participant.userRole || 'Participant'}
                        {participant.isScreenSharing && ' • Sharing'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      {participant.mediaState?.audio ? (
                        <Mic size={16} color="#10b981" />
                      ) : (
                        <MicOff size={16} color="#ef4444" />
                      )}
                      {participant.mediaState?.video || participant.isScreenSharing ? (
                        participant.isScreenSharing ? (
                          <Monitor size={16} color="#10b981" />
                        ) : (
                          <Video size={16} color="#10b981" />
                        )
                      ) : (
                        <VideoOff size={16} color="#ef4444" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={styles.controls}>
        <button
          onClick={toggleAudio}
          style={{
            ...styles.controlButton,
            ...(isAudioEnabled ? styles.activeButton : {}),
            ...(mediaMode === 'viewer' ? { opacity: 0.5, cursor: 'not-allowed' } : {})
          }}
          title={isAudioEnabled ? 'Mute' : 'Unmute'}
          disabled={mediaMode === 'viewer'}
        >
          {isAudioEnabled ? <Mic size={24} color="#fff" /> : <MicOff size={24} color="#fff" />}
        </button>

        <button
          onClick={toggleVideo}
          style={{
            ...styles.controlButton,
            ...(isVideoEnabled && !isSharingScreen ? styles.activeButton : {}),
            ...(mediaMode === 'viewer' || isSharingScreen ? { opacity: 0.5, cursor: 'not-allowed' } : {})
          }}
          title={isVideoEnabled ? 'Stop Video' : 'Start Video'}
          disabled={mediaMode === 'viewer' || isSharingScreen}
        >
          {isVideoEnabled ? <Video size={24} color="#fff" /> : <VideoOff size={24} color="#fff" />}
        </button>

        <button
          onClick={shareScreen}
          style={{
            ...styles.controlButton,
            ...(isSharingScreen ? styles.activeButton : {})
          }}
          title={isSharingScreen ? 'Stop Sharing' : 'Share Screen'}
        >
          {isSharingScreen ? <MonitorOff size={24} color="#fff" /> : <Monitor size={24} color="#fff" />}
        </button>

        <button
          onClick={() => {
            setShowChat(!showChat);
            setShowParticipants(false);
          }}
          style={{
            ...styles.controlButton,
            ...(showChat ? styles.activeButton : {})
          }}
          title="Chat"
        >
          <MessageSquare size={24} color="#fff" />
        </button>

        <button
          onClick={() => {
            setShowParticipants(!showParticipants);
            setShowChat(false);
          }}
          style={{
            ...styles.controlButton,
            ...(showParticipants ? styles.activeButton : {})
          }}
          title="Participants"
        >
          <Users size={24} color="#fff" />
        </button>

        <button
          onClick={leaveMeeting}
          style={{ ...styles.controlButton, ...styles.dangerButton }}
          title="Leave Meeting"
        >
          <Phone size={24} color="#fff" />
        </button>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        button:hover:not(:disabled) {
          transform: scale(1.05);
        }
        
        button:active:not(:disabled) {
          transform: scale(0.95);
        }
      `}</style>
    </div>
  );
};

export default MeetingRoom;